import React from "react";

import PatternSections from "../PatternSections";
import { VerdictBanner } from "../editor/EditorPreview";
import { MatchSection } from "../editor/MatchSections";
import { DECISION, MATCH_SECTION } from "../editor/matchModel";
import StoredMatchRow from "./StoredMatchRow";
import { STORED_MATCH_TEXT as T } from "./catalogStatsText";
import { CATALOG_TEXT, FIX_KIND_LABELS, fullWhen } from "./catalogText";

const C = CATALOG_TEXT.card;
const DEGRADED = "degraded";
const LABELS = Object.freeze({ symptoms: C.symptoms, steps: C.steps, rootCause: C.rootCause, fix: C.fix });

/** True when the entry carries a usable match snapshot. */
export const hasStoredMatch = (match) => Boolean(match) && typeof match === "object";

const rowsOf = (match, key) => (Array.isArray(match[key]) ? match[key].filter((r) => r && typeof r === "object") : [])
  .slice().sort((a, b) => (a.index || 0) - (b.index || 0));
/** The stored row of a single-line section, else the entry's own text as a row saved as new (or null when there is none). */
const singleOf = (match, key, text) => (match[key] && typeof match[key] === "object" ? match[key] : (text ? { text, decision: DECISION.NEW } : null));

function Verdict({ pattern }) {
  if (!pattern || typeof pattern !== "object") return null;
  const existing = pattern.decision === DECISION.EXISTING;
  const title = existing ? (pattern.label ? T.pattern.existing(pattern.label) : T.pattern.existingNoLabel) : T.pattern.fresh;
  return <VerdictBanner existing={existing} title={title} hint={existing ? T.patternHintExisting : T.patternHintNew} />;
}

function Rows({ section, rows }) {
  if (!rows.length) return null;
  return rows.map((line, i) => <StoredMatchRow key={`${section}-${line.index ?? i}`} line={line} />);
}

/**
 * The pattern of a learned entry as the Review screen shows it: the pattern verdict, then symptoms, troubleshooting steps,
 * the root cause and the fix as one kind of read-only row each (StoredMatchRow), from the match snapshot stored when it was
 * saved. An entry without a snapshot (saved before matching, or edited since) shows its plain lines (PatternSections) with a
 * note. Every snapshot field is optional.
 */
export default function CatalogMatch({ item }) {
  const match = item.pattern_match;
  const fixKindLabel = item.fix_kind ? (FIX_KIND_LABELS[item.fix_kind] || item.fix_kind) : "";
  if (!hasStoredMatch(match)) {
    return (
      <div className="le-control">
        <p className="le-note">{T.notMatched}</p>
        <PatternSections labels={LABELS} symptoms={item.symptoms} steps={item.resolution_steps} rootCause={item.root_cause}
          fixKindLabel={fixKindLabel} fixText={item.fix_text} empty={C.none} />
      </div>
    );
  }
  const symptoms = rowsOf(match, MATCH_SECTION.SYMPTOMS);
  const steps = rowsOf(match, MATCH_SECTION.STEPS);
  const cause = singleOf(match, MATCH_SECTION.CAUSE, item.root_cause);
  const fix = singleOf(match, MATCH_SECTION.FIX, item.fix_text);
  const issues = Array.isArray(match.issues) ? match.issues.filter((i) => i && i.message) : [];
  const none = <p className="le-muted">{T.none}</p>;
  return (
    <div className="le-fields" aria-label={T.heading}>
      {match.status === DEGRADED ? <p className="le-note le-note--blocked">{T.degraded}</p> : <Verdict pattern={match.pattern} />}
      <MatchSection label={T.lines.symptoms} count={symptoms.length} after={symptoms.length ? null : none}>
        <Rows section={MATCH_SECTION.SYMPTOMS} rows={symptoms} />
      </MatchSection>
      <MatchSection label={T.lines.steps} count={steps.length} after={steps.length ? null : none}>
        <Rows section={MATCH_SECTION.STEPS} rows={steps} />
      </MatchSection>
      <MatchSection label={T.lines.cause} after={cause ? null : none}>
        <Rows section={MATCH_SECTION.CAUSE} rows={cause ? [cause] : []} />
      </MatchSection>
      <MatchSection label={T.lines.fix} meta={fixKindLabel} after={fix ? null : none}>
        <Rows section={MATCH_SECTION.FIX} rows={fix ? [fix] : []} />
      </MatchSection>
      {issues.length > 0 && (
        <div className="le-control" role="list" aria-label={T.issues}>
          {issues.map((issue, i) => <p key={`${issue.code}-${i}`} role="listitem" className="le-note le-note--blocked">{issue.message}</p>)}
        </div>
      )}
      {match.matched_at && <p className="le-muted">{T.matchedAt(fullWhen(match.matched_at))}</p>}
    </div>
  );
}
